/**
 * Spanish (es) — primary Nexus UI language.
 *
 * Keep keys flat/nested by feature area. When adding new UI strings:
 *   1. Add the key here first.
 *   2. Mirror it in `./en.ts` (fallback is Spanish, but keep parity).
 *   3. Use `useT()` in the component (never inline hardcoded strings
 *      for user-facing copy).
 */
export const es = {
  common: {
    save: 'Guardar',
    cancel: 'Cancelar',
    close: 'Cerrar',
    delete: 'Eliminar',
    edit: 'Editar',
    copy: 'Copiar',
    copied: 'Copiado',
    open: 'Abrir',
    loading: 'Cargando…',
    error: 'Error',
    retry: 'Reintentar',
    back: 'Volver',
    next: 'Siguiente',
    yes: 'Sí',
    no: 'No',
    confirm: 'Confirmar',
    search: 'Buscar',
    settings: 'Configuración',
    upload: 'Subir',
    download: 'Descargar',
    active: 'Activo',
    inactive: 'Inactivo',
    preview: 'Preview',
    syncing: 'Sincronizando',
    retrying: 'Reintentando',
    pending: 'pendiente',
    pendingPlural: 'pendientes',
    ready: 'Listo',
    offline: 'Sin conexión',
    syncRetry: 'Toca para reintentar la sincronización',
  },
  // Sprint P0 Cierre · i18n para estados del engine y sidebar
  engine: {
    idle: 'Listo',
    thinking: 'Pensando…',
    working: 'Trabajando…',
    reviewing: 'Revisando…',
    building: 'Construyendo…',
    ready: 'Listo',
    failed: 'Falló',
    canceled: 'Cancelado',
    queued: 'En cola',
    processing: 'Procesando',
    indexing: 'Indexando',
    scanning: 'Analizando',
    generating: 'Generando…',
  },
  sidebar: {
    home: 'Inicio',
    newProject: 'Nuevo proyecto',
    deployedApps: 'Apps desplegadas',
    recentProjects: 'Proyectos recientes',
    templates: 'Plantillas',
    community: 'Comunidad',
    support: 'Soporte',
    profile: 'Perfil',
    admin: 'Admin',
    logout: 'Cerrar sesión',
  },
  brand: {
    tagline: 'AI Engineering Center',
  },
  // Sprint Estabilización P0.2 · Feb 2026 · avatar menu keys
  menu: {
    myAccount: 'Mi cuenta',
    credits: 'Créditos',
    creditsBilling: 'Créditos y facturación',
    language: 'Idioma',
    voiceAudio: 'Voz y Audio',
    support: 'Soporte',
    contactSupport: 'Contactar soporte',
    supportAi: 'Centro de Soporte IA',
    adminPanel: 'Panel admin',
    admin: 'Administración',
    templates: 'Plantillas',
    userFallback: 'Usuario',
    planPro: 'Pro Plan',
    planFree: 'Free',
  },
  auth: {
    welcome: 'Bienvenido a Nexus',
    subtitle: 'Construye, crea y despliega aplicaciones con inteligencia artificial.',
    continueWithEmail: 'Continuar con correo electrónico',
    continueWithGoogle: 'Continuar con Google',
    noAccount: '¿No tienes cuenta?',
    haveAccount: '¿Ya tienes cuenta?',
    createAccount: 'Crear una cuenta',
    name: 'Nombre',
    secure: 'SEGURO',
    private: 'PRIVADO',
    reliable: 'CONFIABLE',
    email: 'Correo electrónico',
    password: 'Contraseña',
    login: 'Iniciar sesión',
    register: 'Crear cuenta',
    loginError: 'No se pudo iniciar sesión',
    // Sprint P0 Quirúrgico Feb 2026
    showPassword: 'Mostrar contraseña',
    hidePassword: 'Ocultar contraseña',
    forgotPassword: '¿Olvidaste tu contraseña?',
    recoverTitle: 'Recuperar contraseña',
    recoverBody: 'Te enviaremos un enlace para restablecerla.',
    emailPlaceholder: 'tu@correo.com',
    sendRecoveryLink: 'Enviar enlace de recuperación',
    linkGenerated: 'Enlace generado (temporal, 30 min):',
    linkCopied: 'Enlace copiado.',
    openNow: 'Abrir ahora',
    backToLogin: 'Volver a iniciar sesión',
    newPasswordTitle: 'Nueva contraseña',
    newPasswordBody: 'Elige una contraseña de al menos 8 caracteres.',
    newPasswordPlaceholder: 'Nueva contraseña',
    confirmPasswordPlaceholder: 'Confirmar contraseña',
    updatePassword: 'Actualizar contraseña',
    cannotContinue: 'No se pudo continuar. Intenta de nuevo.',
    forgotToastGenerated: 'Enlace de recuperación generado.',
    forgotToastMaybe: 'Si el correo existe, enviaremos un enlace.',
    forgotToastFail: 'No se pudo procesar la solicitud.',
    resetInvalid: 'Enlace de recuperación inválido.',
    resetMin: 'Mínimo 8 caracteres.',
    resetMismatch: 'Las contraseñas no coinciden.',
    resetOk: 'Contraseña actualizada. Inicia sesión.',
    resetFail: 'No se pudo restablecer.',
  },
  home: {
    title: '¿Qué construirás hoy?',
    titlePre: '¿Qué',
    titleMid: 'construirás',
    titlePost: 'hoy?',
    subtitle: 'Nexus convierte tus ideas en aplicaciones increíbles con el poder de la inteligencia artificial.',
    welcomeUser: 'Bienvenido, {name}',
    defaultName: 'creador',
    newProject: 'Nuevo proyecto',
    promptLabel: 'Cuéntame tu idea…',
    promptPlaceholder: 'Describe lo que quieres construir o pega una URL para mejorar un proyecto.',
    build: 'Construir',
    attach: 'Adjuntar',
    attachHint: '{count} archivo(s) adjuntado(s)',
    pasteUrl: 'Pegar URL de proyecto',
    pasteUrlPrompt: 'Pega la URL del proyecto, repo o inspiración:',
    voice: 'Voz',
    stopDictate: 'Detener dictado',
    voiceUnsupported: 'Tu navegador no soporta reconocimiento de voz.',
    micError: 'Error de micrófono',
    urlAdded: 'URL agregada al prompt',
    urlInvalid: 'La URL debe empezar con http:// o https://',
    workshopUrlInvalid: 'Pega una URL de repositorio válida (https:// o git@).',
    send: 'Enviar',
    removeAttach: 'Quitar',
    menu: 'Menú',
    selectedMode: 'Modo seleccionado:',
    modeApps: 'Aplicaciones',
    modeAppsHint: 'Apps y Landing Pages',
    modeAppsPlaceholder: 'Describe tu aplicación o pega un prompt generado por ChatGPT…',
    modeWorkshop: 'Taller',
    modeWorkshopHint: 'Repara proyectos',
    modeWorkshopPlaceholder: 'Pega la URL de tu repositorio de GitHub para analizar y reparar tu proyecto…',
    recentTitle: 'Tus proyectos recientes',
    recentSeeAll: 'Ver todos',
    deployedTitle: 'Aplicaciones desplegadas',
    deployedManage: 'Gestionar destacadas',
    deployedManageTitle: 'Destacar apps en Home',
    deployedManageHint: 'Elige hasta 4 aplicaciones desplegadas para tenerlas siempre visibles.',
    deployedPlaceholderHint: 'Publica tu primera app y aparecerá aquí',
    deployedEmpty: 'Aún no tienes aplicaciones desplegadas.',
    deployedSaved: 'Selección guardada.',
    deployedSaveError: 'No se pudo guardar la selección.',
    deployedMaxFour: 'Máximo 4 aplicaciones destacadas.',
    deployedPinnedList: 'Destacadas ({count}/4)',
    deployedPinnedEmpty: 'Aún no has destacado ninguna app.',
    deployedAllApps: 'Todas tus aplicaciones desplegadas',
    deployedSaveBtn: 'Guardar selección',
    updated: 'Actualizado hace',
    tagApp: 'App',
    tagWeb: 'Web',
    tagWorkshop: 'Taller',
    tipLabel: 'Consejo',
    tipText: 'Puedes empezar con una idea simple y Nexus la hará realidad.',
    timeNow: 'ahora',
    timeMinutes: '{count} min',
    timeHours: '{count} h',
    timeDays: '{count} d',
    quickFullStack: 'App Full Stack',
    quickFullStackHint: 'Aplicaciones web completas con frontend y backend.',
    quickFullStackPrompt: 'Crea una app full-stack con frontend y backend',
    quickBusiness: 'Páginas para negocios',
    quickBusinessHint: 'Sitios web profesionales rápidos y efectivos.',
    quickBusinessPrompt: 'Crea una landing page profesional para un negocio',
    autoAgent: 'Nexus decide automáticamente qué agente usar — crea, edita, corrige o despliega.',
    createError: 'No se pudo crear el workspace',
    // Sprint P0 Quirúrgico Feb 2026 · claves adicionales para Home
    close: 'Cerrar',
    pinned: 'Fijado',
    pin: 'Fijar',
    mockDashboardTitle: 'Dashboard',
    mockDashboardHint: 'Panel de análisis en tiempo real',
    mockShopTitle: 'Tienda',
    mockShopHint: 'E-commerce con carrito y pagos',
    mockLandingTitle: 'Landing',
    mockLandingHint: 'Página profesional con CTA',
    mockChatTitle: 'Chat IA',
    mockChatHint: 'Conversación con asistente',
    fallbackName: 'Sin nombre',
    fallbackRepo: 'Repo',
    // Sprint Estabilización Final · Feb 2026 · tip banner en Home
    tipBody: 'Para obtener mejores resultados, describe tu aplicación con el mayor detalle posible. Si lo prefieres, pídele a ChatGPT que genere un prompt completo y pégalo aquí.',
  },
  dashboard: {
    title: 'Workspaces',
    subtitle: 'Proyectos aislados con memoria y configuración propia',
    empty: 'Aún no tienes proyectos. Crea uno para empezar.',
    emptyTitle: 'No hay workspaces aún',
    emptyHint: 'Crea tu primer workspace para comenzar',
    createNew: 'Crear nuevo',
    newWorkspace: 'Nuevo Workspace',
    createDialogTitle: 'Crear Workspace',
    workspaceNamePlaceholder: 'Mi Proyecto',
    descriptionOptional: 'Descripción (opcional)',
    descriptionPlaceholder: 'Descripción del workspace...',
    creating: 'Creando...',
    delete: 'Eliminar workspace',
    deletePrefix: 'Vas a eliminar',
    deleteSuffix: 'y todos sus datos:',
    deleteMissions: 'Misiones y su historial',
    deleteMemories: 'Memorias',
    deleteIndex: 'Índice del proyecto importado',
    deleteSandbox: 'Archivos del sandbox',
    deleteWarning: 'Esta acción no se puede deshacer.',
    deleteConfirmBtn: 'Eliminar definitivamente',
    deleting: 'Eliminando…',
    deleteConfirm: '¿Eliminar este workspace? La acción es permanente.',
    deleteSuccess: 'Workspace eliminado',
    deleteError: 'Error al eliminar el workspace',
    createSuccess: 'Workspace creado — abriendo…',
    createError: 'Error al crear workspace',
    loadError: 'Error al cargar workspaces',
    timeLessThanHour: 'Hace menos de una hora',
    timeHour: 'Hace {n} hora',
    timeHours: 'Hace {n} horas',
    timeDay: 'Hace {n} día',
    timeDays: 'Hace {n} días',
  },
  workspace: {
    preview: 'Preview',
    previewHint: 'Abrir Preview de desarrollo en una pestaña nueva',
    chat: 'Chat',
    files: 'Archivos',
    history: 'Historial',
    integrations: 'Integraciones',
    publish: 'Publicar',
    publishing: 'Publicando…',
    republish: 'Republicar',
    published: 'Publicado',
    version: 'v{n}',
    copyUrl: 'Copiar URL',
    openLive: 'Abrir URL pública',
    loadError: 'Error al cargar workspace',
    backHome: 'Volver a inicio',
    durable: 'durable',
    durableFiles: 'archivo(s) guardados de forma durable',
    moreActions: 'Más acciones (Archivos · Resumen · Exportar · Rollback)',
  },
  publish: {
    button: 'Publicar',
    inFlight: 'Publicando… esto tarda 30-90 s',
    successCloudflare: '🚀 Publicado en Cloudflare Pages · v{version}',
    successFly: '🚀 Publicado en Fly.io · v{version}',
    successNexus: '🚀 Publicado en Nexus (interno) · v{version}',
    successGeneric: '🚀 Publicado · v{version}',
    fallback: '{host} falló, pero publiqué en Nexus · v{version}',
    failed: 'No pude publicar. Revisa el estado de la app.',
    notReady: 'Aún no está listo: {reasons}',
    urlCopied: 'URL copiada al portapapeles',
    clipboardError: 'No pude copiar al portapapeles',
    summaryCopied: 'Resumen copiado',
  },
  profile: {
    title: 'Perfil',
    signOut: 'Cerrar sesión',
    plan: 'Plan',
    credits: 'Créditos',
    buyCredits: 'Comprar créditos',
    referrals: 'Programa de referidos',
    referralsHint: 'Gana hasta 500 créditos',
    referralsComingSoon: 'Programa de referidos — próximamente',
    configuration: 'Configuración',
    account: 'Configuración de cuenta',
    accountComingSoon: 'Configuración de cuenta — próximamente',
    language: 'Idioma',
    languagePickerTitle: 'Selecciona un idioma',
    help: 'Ayuda',
    appearance: 'Apariencia',
    theme: 'Tema',
    accent: 'Acento',
    neon: 'Neón',
    themeLight: 'Claro',
    themeDark: 'Oscuro',
    themeSystem: 'Sistema',
    logout: 'Cerrar sesión',
    photoComingSoon: 'Cambio de foto — próximamente',
    buyComingSoon: 'Compra de créditos — próximamente',
    // Sprint P0 Lanzamiento · nuevas claves de menú Perfil
    menuAccountTitle: 'Mi cuenta',
    menuAccountSubtitle: 'Información personal, seguridad y personalización.',
    menuCreditsTitle: 'Créditos',
    menuCreditsSubtitle: 'Revisa tu saldo, historial de consumos y compras.',
    menuCreditsAvailable: 'disponibles',
    menuLanguageTitle: 'Idioma',
    menuLanguageSubtitle: 'Cambia el idioma de la interfaz.',
    menuAudioTitle: 'Voz y Audio',
    menuAudioSubtitle: 'Configura la voz de Nexus, sonidos y accesibilidad.',
    menuSupportTitle: 'Soporte',
    menuSupportSubtitle: 'Habla con nosotros por chat cuando lo necesites.',
    menuAdminTitle: 'Panel de administración',
    menuAdminSubtitle: 'Métricas globales, plantillas, motor financiero y soporte.',
    backendEnvironment: 'Entorno backend',
    backendProd: 'Producción',
    backendDev: 'Desarrollo',
    backendActive: 'activo',
    backendChanged: 'Backend cambiado',
    backendChangeFailed: 'No se pudo cambiar el backend',
    photoTooLarge: 'La foto es demasiado grande (máx. 12 MB).',
    photoUpdated: 'Foto actualizada.',
    creditsExhausted: 'Créditos agotados. Recarga créditos para continuar.',
    wakePreview: 'Reactivar Preview',
    wakePreviewHint: 'Vuelve a levantar tu Preview sin reconstruir ni consumir créditos.',
    wakingPreview: 'Reactivando Preview…',
    previewAwake: 'Preview reactivado',
    wakeFailed: 'No se pudo reactivar el Preview',
  },
  integrations: {
    title: 'Integraciones',
    subtitle: 'Conecta servicios externos a tu cuenta. Cada integración se aplica a todos tus workspaces.',
    github: 'GitHub',
    githubHint: 'Necesario para clonar repos privados, Push a GitHub y Deploy desde el chat.',
    githubConnected: 'GitHub conectado',
    githubDisconnected: 'GitHub desconectado',
    githubConnectedAs: 'Conectado como {name}',
    githubRejected: 'Token guardado · rechazado por GitHub',
    tokenPlaceholder: 'Pega tu Personal Access Token de GitHub',
    tokenSaved: 'Token guardado',
    tokenSavedAs: 'Token verificado como {name} · guardado cifrado',
    tokenDeleted: 'Token eliminado',
    tokenError: 'GitHub rechazó el token: {msg}',
    tokenErrorGeneric: 'error desconocido',
    tokenNeedPaste: 'Pega tu Personal Access Token de GitHub',
    tokenDeleteFail: 'No pude eliminar el token',
    tokenDeleteConfirm: '¿Eliminar el token de GitHub? Push a GitHub dejará de funcionar hasta configurar otro.',
    replace: 'Reemplazar',
    delete: 'Eliminar',
    validating: 'Validando…',
    save: 'Guardar',
    cancel: 'Cancelar',
    createLink: 'Creas el token en',
    scopeHint: '(scope repo). Nexus lo valida contra GitHub antes de guardarlo cifrado.',
    comingSoonNote:
      'Próximamente: integraciones adicionales (Stripe, ElevenLabs, Twilio…). Si necesitas una integración específica, cuéntanoslo.',
    back: 'Volver',
  },
  delivery: {
    delivered: 'Entrega',
    listenSummary: 'Escuchar resumen',
    stop: 'Detener',
    copySummary: 'Copiar resumen',
    showDetails: 'Ver detalles',
    hideDetails: 'Ocultar detalles',
    changes: 'Cambios',
    time: 'Tiempo',
    patches: 'Cambios aplicados',
    verifs: 'Verificaciones',
    evidence: 'Archivos citados',
    iterations: 'Iteraciones',
    modifiedFiles: 'Archivos modificados',
    andMore: '… y {count} más',
    ttsError: 'No pude generar la voz. Inténtalo de nuevo.',
    titleObservations: 'Trabajo con observaciones',
    titleModApplied: 'Modificación aplicada',
    titlePublished: 'Publicación completada',
    titleAnalyzed: 'Análisis completado',
    titleAppReady: 'Tu app está lista',
    titleDone: 'Trabajo completado',
    recapImplemented: 'Lo que implementé',
    recapFindings: 'Lo que encontré durante el desarrollo',
    recapRecommendations: 'Recomendaciones',
  },
  workshop: {
    loading: 'Cargando…',
    delete: 'Eliminar sesión',
    deleteConfirm: '¿Eliminar esta sesión del Taller? El sandbox local también se borrará.',
    deleted: 'Sesión eliminada',
    deleteError: 'No pude eliminar la sesión',
    phaseCloning: 'Clonando tu repositorio…',
    phaseAnalyzing: 'Analizando la estructura de tu proyecto…',
    failureTitle: 'No pude terminar el análisis',
    retry: 'Reintentar',
    analysisTitle: 'Diagnóstico',
    framework: 'Framework',
    runtime: 'Runtime',
    entryPoint: 'Punto de entrada',
    services: 'Servicios detectados',
    notDetected: 'No detectado',
    none: 'Ninguno',
    canBoot: 'Tu proyecto está listo para intentar levantar el Preview.',
    needsHelp: 'Necesito más información antes de intentar el Preview.',
    findingsTitle: '{count} hallazgo(s)',
    branchTitle: 'Rama de trabajo',
    branchHint: 'Todos los cambios se aplican en esta rama — nunca en main.',
    envTitle: 'Variables requeridas',
    envHint: 'Pega los valores de las variables faltantes (KEY=VALOR por línea).',
    envSave: 'Guardar variables',
    envSaved: '{count} variable(s) guardada(s)',
    envInvalid: 'Formato esperado: NOMBRE=valor por línea.',
    envError: 'No pude guardar las variables',
    actionsTitle: 'Acciones',
    repairBtn: 'Iniciar reparación',
    repairInFlight: 'Iniciando reparación…',
    repairReady: 'Preview listo. Puedo empezar a reparar los problemas encontrados.',
    repairBlocked: 'Primero necesito lograr que tu app arranque en el Preview.',
    repairStarted: 'Reparación iniciada',
    repairError: 'No pude iniciar la reparación',
    statusCloning: 'Clonando',
    statusAnalyzing: 'Analizando',
    statusReady: 'Listo',
    statusPreviewOk: 'Preview OK',
    statusRepairing: 'Reparando',
    statusReadyPush: 'Listo para publicar',
    statusPushed: 'Publicado',
    statusFailed: 'Falló',
  },
  support: {
    title: 'Centro de Soporte',
    titleAdmin: 'Soporte · Panel administrador',
    newThread: 'Nueva conversación',
    newHint: 'Escríbenos y responderemos lo antes posible. Puedes adjuntar imágenes o archivos en cualquier mensaje.',
    subjectPlaceholder: 'Asunto (ej. Mi despliegue falló)',
    messagePlaceholder: 'Cuéntanos qué está pasando…',
    replyPlaceholder: 'Escribe un mensaje…',
    send: 'Enviar',
    attach: 'Adjuntar',
    closeThread: 'Cerrar hilo',
    pickThreadHint: 'Selecciona una conversación de la izquierda o crea una nueva.',
    emptyUser: 'Aún no tienes conversaciones abiertas.',
    emptyAdmin: 'No hay conversaciones para revisar.',
    threadCreated: 'Conversación creada',
    errorCreate: 'No pude crear la conversación',
    errorSend: 'No pude enviar el mensaje',
    errorFieldsRequired: 'Completa el asunto y el mensaje',
    attachTooLarge: '{name} supera los 4 MB',
    statusOpen: 'Abierto',
    statusAnswered: 'Respondido',
    statusInProgress: 'En progreso',
    statusClosed: 'Cerrado',
    statusUnknown: 'Estado',
    teamName: 'Equipo Nexus',
  },
  // Sprint P0 Quirúrgico Feb 2026 · claves añadidas para completar i18n
  workspaceUser: {
    deliveryReady: 'Aplicación funcional entregada · Preview lista',
    projectReady: 'Tu proyecto está listo',
    pending: 'Pendiente',
    recommendations: 'Recomendaciones',
    publish: 'Publicar',
    publishSandbox: 'Publicar a Sandbox',
    promoteProduction: 'Promover a Producción',
    copy: 'Copiar',
    preview: 'Preview',
    rollback: 'Rollback',
    engineRunning: 'Nexus está trabajando…',
    engineRunningLabel: 'Motor en ejecución',
    cancel: 'Cancelar',
    nexusAi: 'Nexus IA · ',
    thinking: 'Pensando…',
    composerPlaceholder: 'Escribe un mensaje…',
    composerDisabled: 'Motor trabajando…',
    newMessages: 'Nuevos mensajes',
    startPrompt: 'Empieza describiendo tu idea. Nexus se pondrá a trabajar.',
    loadError: 'No se pudo cargar el workspace.',
    nameUpdated: 'Nombre actualizado',
    previewPreparing: 'Preparando el Preview…',
    backHome: 'Volver al inicio',
    rename: 'Renombrar',
    workspaceFallback: 'Workspace',
    statusActive: 'Activo',
    statusLoading: 'Cargando',
    editModeHint: 'Abre el Preview con las herramientas de edición activas',
    editMode: 'Modo edición',
    attach: 'Adjuntar',
    voiceStart: 'Dictar por voz',
    voiceStop: 'Detener voz',
    sendAria: 'Enviar',
    cancelBuild: 'Cancelar construcción',
    buildCanceled: 'Construcción cancelada',
    buildCanceledDetail: 'Detuviste la construcción actual. Puedes enviar una nueva instrucción cuando quieras.',
    composerWorking: 'Nexus está trabajando… espera al Delivery Card',
    composerType: 'Escribe tu mensaje…',
    publishSandboxOk: 'Publicado al Sandbox correctamente.',
    publishSandboxRegistered: 'Solicitud registrada. Configura NEXUS_SANDBOX_WEBHOOK para deploy real.',
    publishSandboxFail: 'No se pudo publicar al Sandbox.',
    promoteConfirm: '¿Promover la última versión del Sandbox a Producción?\n\nEsta acción notifica al pipeline de deploy productivo.',
    promoteProdOk: 'Promovido a Producción correctamente.',
    promoteProdRegistered: 'Promoción registrada. Configura NEXUS_PROD_WEBHOOK para deploy real.',
    promoteProdFail: 'No se pudo promover a Producción.',
    changesDone: 'Cambios realizados: ',
    pendingLabel: 'Pendientes: ',
    recommendationsLabel: 'Recomendaciones: ',
    ttsRetap: 'Toca reproducir de nuevo para escuchar',
    ttsPreparing: 'Preparando…',
    ttsStop: 'Detener',
    ttsListen: 'Escuchar',
    ttsError: 'No se pudo reproducir el audio.',
    renameFail: 'No se pudo renombrar.',
    attachFail: 'No se pudo adjuntar.',
    // Sprint P0 Quirúrgico Feb 2026 · estados rotatorios del indicador vivo
    statusAnalyzing: '🧠 Analizando…',
    statusDesigning: '🎨 Diseñando…',
    statusBuilding: '🧱 Construyendo…',
    statusIntegrating: '⚙️ Integrando…',
    statusTesting: '🧪 Ejecutando pruebas…',
    statusCompiling: '📦 Compilando…',
    statusPreview: '🚀 Generando Preview…',
    statusValidating: '✅ Validando entrega…',
  },
  rollback: {
    confirmTitle: '¿Restaurar la versión anterior?',
    confirmBody: 'Se restaurará la versión anterior de tu Preview y se descartarán los cambios generados más recientes. Los créditos ya consumidos en generaciones previas no serán reembolsados. Esta acción no se puede deshacer.',
    confirmCta: 'Restaurar versión anterior',
    cancel: 'Cancelar',
    inFlight: 'Restaurando…',
    success: 'Preview restaurado a la versión anterior',
    empty: 'No hay una versión anterior guardada todavía',
    error: 'No pude restaurar el Preview',
  },
  credits: {
    label: 'Créditos:',
    refill: 'Recargar créditos',
    negative: 'Debes',
    balance: 'Saldo',
    // Sprint Estabilización Final · Feb 2026 · página completa /creditos
    pageTitle: 'Créditos',
    availableCredits: 'CRÉDITOS DISPONIBLES',
    creditsWord: 'créditos',
    renewal: 'Renovación',
    pickPackage: 'Elige tu paquete de créditos',
    pickPackageSub: 'Más créditos, más posibilidades.',
    trustStripSub: 'Transacciones rápidas, protegidas y confiables.',
    buyCredits: 'Comprar créditos',
    chooseAmount: 'Elige el importe',
    plan: 'Plan',
    planFree: 'Free',
    planPro: 'Pro',
    addCreditsCta: 'Añadir créditos',
    packagesTitle: 'Paquetes de créditos',
    packageMostPopular: 'MÁS POPULAR',
    packageBuy: 'Comprar',
    trustStripTitle: 'Pagos 100% seguros con Stripe',
    trustSecure: 'Pago seguro',
    trustFast: 'Proceso rápido',
    trustPrivacy: 'Protección de datos',
    customAmountTitle: 'Otra cantidad',
    customAmountBody: 'Elige exactamente la cantidad que necesitas.',
    customAmountCta: 'Elige cantidad',
    historyTitle: 'Historial de recargas',
    historyBody: 'Revisa tus compras anteriores.',
    footerNote: 'Tus créditos se activan al instante después del pago.',
    back: 'Volver',
    close: 'Cerrar',
    purchaseTitle: 'Confirmar compra',
    purchaseCta: 'Pagar con Stripe',
    purchaseBusy: 'Redirigiendo…',
    purchaseFail: 'No se pudo iniciar el pago',
    successTitle: '¡Compra realizada!',
    successBody: 'Tus créditos ya están disponibles.',
    successCta: 'Continuar',
    packageDesc100: 'Ideal para probar y ajustes rápidos',
    packageDesc250: 'Perfecto para proyectos pequeños',
    packageDesc500: 'Ideal para proyectos pequeños y medianos',
    packageDesc1250: 'Excelente para trabajo continuo',
    packageDesc2500: 'Óptimo para equipos y agencias',
    packageDesc5000: 'Máximo aprovechamiento',
  },
  settings: {
    title: 'Configuración',
    back: 'Volver',
    navProfile: 'Perfil',
    navSecurity: 'Seguridad',
    navAppearance: 'Apariencia',
    navNotifications: 'Notificaciones',
    navIntegrations: 'Integraciones',
    navApikeys: 'API Keys',
    navPreferences: 'Preferencias',
    navLanguage: 'Idioma',
    navVoice: 'Voz y Audio',
    navPrivacy: 'Datos y privacidad',
    profileTitle: 'Perfil',
    photoHint: 'Foto sincronizada con tu correo. Podrás subir una imagen personalizada próximamente.',
    fieldName: 'Nombre',
    fieldEmail: 'Correo electrónico',
    fieldPlan: 'Plan',
    planAdmin: 'Admin · Pro Plan',
    planFree: 'Free',
    saveChanges: 'Guardar cambios',
    profileSaved: 'Perfil actualizado',
    profileSaveFail: 'No pude guardar',
    securityTitle: 'Seguridad',
    curPassword: 'Contraseña actual',
    newPassword: 'Nueva contraseña (mín. 8 caracteres)',
    updatePassword: 'Actualizar contraseña',
    passwordTooShort: 'Contraseña muy corta (mín. 8)',
    passwordUpdated: 'Contraseña actualizada',
    passwordUpdateFail: 'No pude actualizar la contraseña',
    passwordEndpointSoon: 'Endpoint de cambio de contraseña próximamente',
    appearanceTitle: 'Apariencia',
    appearanceBody: 'Nexus utiliza el tema oscuro Cyan Neón por defecto. Próximamente podrás cambiar el acento entre cyan, morado, verde neón o rojo, y subir un fondo personalizado a tu Home.',
    themeLabel: 'Tema',
    themeDarkCyan: 'Oscuro (Cyan Neón)',
    themeAuto: 'Automático (según sistema)',
    languageTitle: 'Idioma',
    languageBody: 'Nexus habla tu idioma. Los mensajes del motor, la interfaz y las voces se adaptan automáticamente a tu selección.',
    languageField: 'Idioma de la interfaz',
    voiceTitle: 'Voz y Audio',
    voiceBody: 'Elige cómo suena Nexus. La voz seleccionada se usa en la "Escuchar resumen" del chat.',
    voiceField: 'Voz por defecto',
    voiceUpdated: 'Voz por defecto actualizada',
    voiceUpdateFail: 'No pude cambiar la voz',
    apikeysTitle: 'API Keys',
    apikeysBody: 'Nexus utiliza la Emergent Universal Key para todos los modelos de IA. No necesitas gestionar tus propias claves. Para integraciones externas (Stripe, GitHub, etc.) visita Configuración → Integraciones.',
    integrationsTitle: 'Integraciones',
    integrationsBody: 'Conecta Nexus a GitHub, Stripe, servicios de correo y más. El motor detecta y utiliza los servicios conectados automáticamente.',
    integrationsOpen: 'Ir al panel de Integraciones →',
    notificationsBody: 'Configura alertas por email y en la app para cambios de créditos, deploys y respuestas de soporte.',
    preferencesBody: 'Personaliza el flujo autónomo de Nexus (auto-deploy, verificadores, tolerancia a riesgos).',
    privacyBody: 'Exporta tus datos, gestiona sesiones activas y elimina tu cuenta si lo deseas.',
    comingSoon: 'Panel disponible en próxima iteración.',
  },
  idioma: {
    title: 'Idioma',
    back: 'Volver',
    changedEs: 'Idioma cambiado a Español',
    changedEn: 'Language changed to English',
    autoDetectHint: 'La primera vez, Nexus detecta el idioma de tu navegador automáticamente. Aquí puedes cambiarlo cuando quieras.',
  },
  audio: {
    title: 'Voz y Audio',
    back: 'Volver',
    sectionDefaultVoice: 'Voz predeterminada',
    sectionSpeed: 'Velocidad',
    sectionToggles: 'Activar / desactivar',
    switchAutoplay: 'Reproducción automática de la voz',
    switchMic: 'Micrófono',
    switchSystemSounds: 'Sonidos del sistema',
    switchHiContrast: 'Alto contraste (accesibilidad)',
    savedLocally: 'Los cambios se guardan automáticamente en este dispositivo.',
    fallbackJarvisName: 'Asistente Futurista',
    fallbackJarvisDesc: 'Voz profunda y serena, estilo JARVIS',
    fallbackNovaName: 'Voz Natural',
    fallbackNovaDesc: 'Voz humana cálida y natural, ideal para lectura conversacional',
    fallbackFableName: 'Humanoide con Carácter',
    fallbackFableDesc: 'Voz distintiva con timbre británico, personalidad marcada',
    playAria: 'Reproducir muestra de {name}',
    pauseAria: 'Pausar muestra de {name}',
    defaultSet: 'Voz predeterminada: {name}',
    noCredits: 'Sin créditos para audio',
    sampleFail: 'No se pudo generar la muestra',
    retap: 'Toca reproducir de nuevo para escuchar',
    playFail: 'No se pudo reproducir el audio',
    sample: 'Hola, soy tu asistente de Nexus. Puedo leer tus mensajes en voz alta.',
  },
};

// Cast avoids TS `as const` locking every string as a literal so
// English/Portuguese/… dictionaries can freely use their own text.
export type Dictionary = {
  [K in keyof typeof es]: { [J in keyof (typeof es)[K]]: string };
};
